import React from 'react';
import type { PageMockKind } from '../data/lookInsidePages';
import { RinkLanes } from './RinkLanes';

/**
 * Landscape page previews built in CSS with the pack's identity:
 * block bands (A navy · B glacier · C lilac), 12 px cards, lane pills.
 * Uses container-relative sizing so it scales from carousel to lightbox.
 */

function Band({
  tone,
  label,
  page
}: {
  tone: 'a' | 'b' | 'c';
  label: string;
  page: string;
}) {
  const bg = tone === 'a' ? 'bg-rink text-white' : tone === 'b' ? 'bg-glacier text-rink-deep' : 'bg-lilac text-white';
  return <div className={`flex items-center justify-between px-[4%] py-[1.6%] text-[0.62em] font-semibold uppercase tracking-[0.16em] ${bg}`} data-id="element-202" data-mp="117q3za6844zq">
      <span data-id="element-203" data-mp="1awzz6ywd63s2">{label}</span>
      <span className="opacity-80" data-id="element-204" data-mp="1aybbjn3vkjus">Vol. 1 · p. {page}</span>
    </div>;
}
function Pill({
  lane
}: {
  lane: 1 | 2 | 3;
}) {
  const c = lane === 1 ? 'bg-mint' : lane === 2 ? 'bg-amber' : 'bg-coral';
  return <span className={`inline-flex items-center rounded-full px-[0.7em] py-[0.15em] font-display text-[0.7em] font-bold text-rink-deep ${c}`} data-id="element-205" data-mp="ifnvim3qjv0k">
      L{lane}
    </span>;
}
function Cover() {
  return <div className="flex h-full flex-col bg-rink text-white" data-id="element-206" data-mp="yd78oz21ghzq">
      <div className="grid flex-1 grid-cols-[1.1fr_1fr] items-center gap-[4%] px-[6%]" data-id="element-207" data-mp="tknwa5f37swi">
        <div data-id="element-208" data-mp="pju8dc1nae3m">
          <p className="text-[0.7em] font-semibold uppercase tracking-[0.2em] text-amber" data-id="element-209" data-mp="pk41h8hzl1dj">Volume 1 of 6 · Weeks 1–9</p>
          <p className="mt-[4%] font-display text-[2.1em] font-bold leading-[1.05]" data-id="element-210" data-mp="kliw6unvnri8">First Glides</p>
          <p className="mt-[3%] text-[0.85em] leading-snug text-white/80" data-id="element-211" data-mp="fkd7r9192qei">
            Every New Skater Gliding, Stopping and Stroking
          </p>
          <p className="mt-[6%] text-[0.62em] font-semibold uppercase tracking-[0.14em] text-glacier" data-id="element-212" data-mp="ao4rttf8at6a">
            9 full lessons · 10 extra games · 3 worksheets
          </p>
        </div>
        <div className="rounded-card bg-rink-deep p-[6%]" data-id="element-213" data-mp="24y8fceufzsx1">
          <RinkLanes labels={false} title="Rink with three lanes" data-id="element-214" data-mp="20zskxpw17i9l" />
        </div>
      </div>
      <div className="flex items-center justify-between px-[6%] pb-[3%] text-[0.62em] font-semibold uppercase tracking-[0.16em] text-white/60" data-id="element-215" data-mp="3ugf4udc3xmhg">
        <span data-id="element-216" data-mp="34vi1z2t8sgp">Figure Skating Class Ready</span>
        <span className="flex h-[0.5em] w-[30%]" data-id="element-217" data-mp="3lhl368jx65x1">
          <span className="flex-1 bg-mint" data-id="element-218" data-mp="3lep5mlbzhj94" />
          <span className="flex-1 bg-amber" data-id="element-219" data-mp="3phls4n4n9zic" />
          <span className="flex-1 bg-coral" data-id="element-220" data-mp="3pgamsivjwjp4" />
        </span>
      </div>
    </div>;
}
function IceMap() {
  return <div className="flex h-full flex-col bg-ice text-ink" data-id="element-221" data-mp="m1oeg479y9f9">
      <Band tone="a" label="A · Set-up" page="6" data-id="element-222" data-mp="m14uyj1z7cz4" />
      <div className="grid flex-1 grid-cols-[1.5fr_1fr] gap-[4%] px-[4%] py-[3%]" data-id="element-223" data-mp="825i08ntsmis">
        <div data-id="element-224" data-mp="8489yy8jgpq5">
          <p className="font-display text-[1.25em] font-bold leading-tight text-rink" data-id="element-225" data-mp="d0q9njp0hxvm">The Ice Map</p>
          <p className="text-[0.72em] text-ink-soft" data-id="element-226" data-mp="2sse2p40wn695">Where each lane skates, waits and rotates</p>
          <div className="mt-[3%]" data-id="element-227" data-mp="2xqxsxgmj747b">
            <RinkLanes title="The Ice Map: three lanes on the rink" data-id="element-228" data-mp="2xrz3zl3jrqp1" />
          </div>
        </div>
        <ul className="grid content-center gap-[6%] text-[0.68em] leading-snug" data-id="element-229" data-mp="2p8vr3ygceny4">
          <li className="rounded-card bg-white p-[6%] shadow-card" data-id="element-230" data-mp="2l9d5f0gp68u8"><Pill lane={1} data-id="element-231" data-mp="2lay1iuintzu7" /> Door-side boards. Shortest distances, wall within reach.</li>
          <li className="rounded-card bg-white p-[6%] shadow-card" data-id="element-232" data-mp="2z6a7jy29ncqf"><Pill lane={2} data-id="element-233" data-mp="2u906tm9ffena" /> Center. Full length, one direction.</li>
          <li className="rounded-card bg-white p-[6%] shadow-card" data-id="element-234" data-mp="26jp8ebfjtwcb"><Pill lane={3} data-id="element-235" data-mp="26k8mfh22w0l2" /> Far boards. Speed and full patterns.</li>
          <li className="text-ink-soft" data-id="element-236" data-mp="21ldwlv5p1brg">Whistle = rotate · star = coach watch point</li>
        </ul>
      </div>
    </div>;
}
function LessonCard() {
  return <div className="flex h-full flex-col bg-ice text-ink" data-id="element-237" data-mp="2gbblyefd07i8">
      <Band tone="b" label="B · Lessons · Lesson Card" page="18" data-id="element-238" data-mp="2bi0hul1xm4u0" />
      <div className="flex-1 px-[4%] py-[3%]" data-id="element-239" data-mp="1nso5xdf7zio0">
        <p className="text-[0.62em] font-semibold uppercase tracking-[0.16em] text-glacier-deep" data-id="element-240" data-mp="1nrd7lt4jn34a">Week 4</p>
        <p className="font-display text-[1.2em] font-bold leading-tight text-rink" data-id="element-241" data-mp="1is0i10mafhy0">
          Stops every skater can use on the first try
        </p>
        <div className="mt-[3%] grid grid-cols-3 gap-[3%] text-[0.66em]" data-id="element-242" data-mp="r11bqqshkf2f">
          {[['Goal', 'A controlled stop on a signal, at every level'], ['Length', '30 · 45 · 60 min'], ['Material', 'Cones · whistle']].map(([k, v]) => <div key={k} className="rounded-card bg-white p-[8%] shadow-card" data-id="element-243" data-mp="14thf5spauf3v">
              <span className="block text-[0.85em] font-semibold uppercase tracking-widest text-ink-muted" data-id="element-244" data-mp="14vlgc2vtd00g">{k}</span>
              <span className="mt-[4%] block font-medium" data-id="element-245" data-mp="1eroktfe51aqk">{v}</span>
            </div>)}
        </div>
        <div className="mt-[3%] rounded-card border-l-4 border-amber bg-white p-[3%] text-[0.68em] shadow-card" data-id="element-246" data-mp="1itih7bhavskx">
          <span className="font-display font-bold text-rink" data-id="element-247" data-mp="3gjtk5bxyvabb">Warm-up game · Stop Challenge</span>
          <span className="block text-ink-soft" data-id="element-248" data-mp="3kjlmv80oim8r">
            Purpose: stop on the whistle, not on the wall. Each lane plays its own version.
          </span>
        </div>
      </div>
    </div>;
}
function Circuit() {
  const rows: {
    lane: 1 | 2 | 3;
    skill: string;
    cue: string;
    error: string;
  }[] = [{
    lane: 1,
    skill: 'Snowplow stop',
    cue: 'Bend the knees, push both heels out',
    error: 'Stiff, locked knees'
  }, {
    lane: 2,
    skill: 'One-foot snowplow stop, both sides',
    cue: 'Sit into the stopping knee',
    error: 'Leaning forward over the toe pick'
  }, {
    lane: 3,
    skill: 'T-stop and two-foot hockey stop',
    cue: 'Free blade behind at a right angle',
    error: 'Dragging on the toe pick'
  }];
  return <div className="flex h-full flex-col bg-ice text-ink" data-id="element-249" data-mp="3mxpc55e8bmb6">
      <Band tone="b" label="B · Lessons · 3-Lane Circuit" page="19" data-id="element-250" data-mp="3i07ck5m3qjfu" />
      <div className="flex-1 px-[4%] py-[3%]" data-id="element-251" data-mp="3i21nqs7kmx6q">
        <p className="font-display text-[1.2em] font-bold leading-tight text-rink" data-id="element-252" data-mp="2bk537osl92od">Week 4 circuit: one skill, three versions</p>
        <div className="mt-[3%] grid grid-cols-3 gap-[3%] text-[0.64em] leading-snug" data-id="element-253" data-mp="26j79qg24om7k">
          {rows.map(r => <div key={r.lane} className="rounded-card bg-white p-[7%] shadow-card" data-id="element-254" data-mp="27zuuh3ys6eky">
              <Pill lane={r.lane} data-id="element-255" data-mp="2d0ilvqqx8xfp" />
              <p className="mt-[6%] font-display font-bold text-rink" data-id="element-256" data-mp="1z19gzn861pr1">{r.skill}</p>
              <p className="mt-[6%]" data-id="element-257" data-mp="1z3c33w676yqh"><span className="font-semibold" data-id="element-258" data-mp="232kwgzxbsuli">Cue:</span> {r.cue}</p>
              <p className="mt-[4%] text-coral-deep" data-id="element-259" data-mp="234xo64svyjbr"><span className="font-semibold" data-id="element-260" data-mp="1pbpso21op3os">Error:</span> {r.error}</p>
            </div>)}
        </div>
        <p className="mt-[3%] text-[0.62em] text-ink-soft" data-id="element-261" data-mp="1u2fzljprdell">Rotate on the whistle · easier / harder switch for every lane</p>
      </div>
    </div>;
}
function FieldCard() {
  return <div className="flex h-full flex-col bg-ice text-ink" data-id="element-262" data-mp="1kbtr09ggloce">
      <Band tone="c" label="Reference · Field card" page="50" data-id="element-263" data-mp="1kaab4jyptfb5" />
      <div className="grid flex-1 grid-cols-2 gap-[3%] px-[4%] py-[3%] text-[0.64em] leading-snug" data-id="element-264" data-mp="16dn23ithmyov">
        <div className="rounded-card bg-white p-[5%] shadow-card" data-id="element-265" data-mp="1bana3z9webim">
          <p className="font-display text-[1.25em] font-bold text-rink" data-id="element-266" data-mp="rpn33tctpzx6">The 3-Lane formula</p>
          <ol className="mt-[4%] grid gap-[3%]" data-id="element-267" data-mp="mr1xfeesyo1c">
            <li data-id="element-268" data-mp="msd3jkvq07vm">1 · Warm-up game</li>
            <li data-id="element-269" data-mp="10ql5k77m5zdn">2 · 3-Lane Circuit</li>
            <li data-id="element-270" data-mp="10rmhedwgepwf">3 · Finish & Show-off</li>
          </ol>
        </div>
        <div className="rounded-card bg-white p-[5%] shadow-card" data-id="element-271" data-mp="wp803y8fny79">
          <p className="font-display text-[1.25em] font-bold text-rink" data-id="element-272" data-mp="wr2m7fvfxwcx">First-weeks checklist</p>
          <ul className="mt-[4%] grid gap-[3%]" data-id="element-273" data-mp="1amwhux77ep83">
            <li data-id="element-274" data-mp="15qfx8cz7qszr">✓ Lanes marked with cones</li>
            <li data-id="element-275" data-mp="15peavccqyk2q">✓ Lane Sort roster ready</li>
            <li data-id="element-276" data-mp="1kf2bebd3tepe">✓ Whistle signals explained</li>
          </ul>
        </div>
        <div className="col-span-2 rounded-card bg-rink p-[3%] text-white" data-id="element-277" data-mp="1kgdhwixxp0k7">
          <span className="font-display font-bold text-amber" data-id="element-278" data-mp="1fg95p5426rzs">Top 10 cues</span>
          <span className="ml-[2%] text-white/80" data-id="element-279" data-mp="1tg0e8qbt3g6e">Knees soft · eyes up · push and glide · arms at the table…</span>
        </div>
      </div>
    </div>;
}
export function PageMock({
  kind
}: {
  kind: PageMockKind;
}) {
  return <div className="w-full [container-type:inline-size]" data-id="element-280" data-mp="2vupexgc8s186">
      <div className="aspect-[16/10] w-full overflow-hidden text-left" style={{
      fontSize: '2.4cqw'
    }} data-id="element-281" data-mp="30qmyrwmprppp">
        {kind === 'cover' && <Cover data-id="element-282" data-mp="34u2qcejxezvj" />}
        {kind === 'icemap' && <IceMap data-id="element-283" data-mp="39s4al2t3958l" />}
        {kind === 'lessoncard' && <LessonCard data-id="element-284" data-mp="39r2uul14nukj" />}
        {kind === 'circuit' && <Circuit data-id="element-285" data-mp="2d23qhh3nzcxm" />}
        {kind === 'fieldcard' && <FieldCard data-id="element-286" data-mp="2d3eqr6xwi2jz" />}
      </div>
    </div>;
}